import {
  CButton,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CModalFooter,
} from "@coreui/react"
import {
  DatePicker,
  Flex,
  Input,
  Select,
  TimePicker,
  Typography,
  Upload,
  Button,
  Tag,
  Space,
  Card,
  Form,
} from "antd"
import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getClubs, getGyms, getTrainers } from "src/store/Slices/DropDownSlice"
import { getServices } from "src/store/Slices/AbonementsSlice"
import { scheduleAction } from "src/store/Slices/Administrator/ScheduleSlice"
import dayjs from "dayjs"
import { UploadOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons"
import { openNotification } from "src/components/helpers/notification"

const { TextArea } = Input

const AddEditScheduleModal = ({
  visible,
  setVisible,
  scheduleInfo,
  setPage,
  setPageSize,
  payload,
}) => {
  const dispatch = useDispatch()
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [date, setDate] = useState("")
  const [duration, setDuration] = useState("")
  const [service, setService] = useState("")
  const [club, setClub] = useState("")
  const [trainer, setTrainer] = useState("")
  const [limit, setLimit] = useState("")
  const [imgUrl, setImgUrl] = useState("")
  const [gym, setGym] = useState("")
  const [price, setPrice] = useState("")
  const [fileList, setFileList] = useState([])
  const [uploading, setUploading] = useState(false)
  const [tagInput, setTagInput] = useState("")
  const [tags, setTags] = useState([])
  const [benefits, setBenefits] = useState([
    { BenefitText: "", BenefitDescription: "" },
  ])

  const clubs = useSelector((state) => state.DropDownSlice.clubs)
  const abonementServices = useSelector(
    (state) => state.AbonementsSlice.abonementServices,
  )
  const trainersList = useSelector((state) => state.DropDownSlice.trainersList)
  const gyms = useSelector((state) => state.DropDownSlice.gyms)

  const handleBenefitChange = (index, field, value) => {
    const newBenefits = [...benefits]
    newBenefits[index][field] = value
    setBenefits(newBenefits)
  }

  const addBenefit = () => {
    setBenefits([...benefits, { BenefitText: "", BenefitDescription: "" }])
  }

  const removeBenefit = (index) => {
    if (benefits.length > 1) {
      const newBenefits = benefits.filter((_, i) => i !== index)
      setBenefits(newBenefits)
    }
  }

  useEffect(() => {
    setName("")
    setDescription("")
    setDate("")
    setDuration("")
    setService("")
    setClub("")
    setTrainer("")
    setLimit("")
    setImgUrl("")
    setGym("")
    setPrice("")
    setFileList([])
    setTags([])
    setTagInput("")
    setBenefits([{ BenefitText: "", BenefitDescription: "" }])
  }, [visible.flag])

  useEffect(() => {
    if (visible.flag === "edit" && scheduleInfo) {
      setName(scheduleInfo?.name)
      setDescription(scheduleInfo?.description || "")
      setClub(
        clubs?.data?.value.find((el) => el.label === scheduleInfo?.clubName)
          ?.value,
      )
      setService(
        abonementServices?.data?.value.find(
          (el) => el.label === scheduleInfo?.serviceName,
        )?.value,
      )
      setTrainer(
        trainersList?.data?.value.find(
          (el) => el.label === scheduleInfo?.employName,
        )?.value,
      )
      setGym(
        gyms?.data?.value.find((el) => el.label === scheduleInfo?.className)
          ?.value,
      )
      setLimit(scheduleInfo?.limit)
      setImgUrl(scheduleInfo?.imageUrl?.props?.src)
      setPrice(scheduleInfo?.price || null)
      setFileList([])

      if (scheduleInfo?.tags) {
        if (typeof scheduleInfo.tags === "string") {
          const tagsArray = scheduleInfo.tags
            .split(",")
            .map((tag) => tag.trim())
            .filter((tag) => tag !== "")
          setTags(tagsArray)
        } else if (Array.isArray(scheduleInfo.tags)) {
          setTags(scheduleInfo.tags)
        } else if (
          typeof scheduleInfo.tags === "string" &&
          scheduleInfo.tags.startsWith("[")
        ) {
          try {
            const parsedTags = JSON.parse(scheduleInfo.tags)
            if (Array.isArray(parsedTags)) {
              setTags(parsedTags)
            }
          } catch (error) {
            setTags([])
          }
        } else {
          setTags([])
        }
      } else {
        setTags([])
      }

      if (scheduleInfo?.benefits && Array.isArray(scheduleInfo.benefits)) {
        setBenefits(scheduleInfo.benefits)
      } else {
        setBenefits([{ BenefitText: "", BenefitDescription: "" }])
      }
    }
  }, [visible, scheduleInfo, clubs, trainersList, abonementServices, gyms])

  useEffect(() => {
    if (visible.flag === "edit") {
      dispatch(getClubs())
      dispatch(getTrainers())
      dispatch(getServices())
      dispatch(getGyms())
    }
  }, [visible])

  const handleTagInputChange = (e) => {
    const value = e.currentTarget.value
    setTagInput(value)
  }

  const handleTagInputKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === ";") {
      e.preventDefault()
      addTags()
    }
  }

  const addTags = () => {
    if (!tagInput.trim()) return

    const newTags = tagInput
      .split(/[,;]/)
      .map((tag) => tag.trim())
      .filter((tag) => tag !== "" && !tags.includes(tag))

    if (newTags.length > 0) {
      const cleanExistingTags = tags.filter((tag) => tag != null && tag !== "")
      const updatedTags = [...cleanExistingTags, ...newTags]
      setTags(updatedTags)
      setTagInput("")
    }
  }

  const removeTag = (indexToRemove) => {
    const updatedTags = tags.filter((_, index) => index !== indexToRemove)
    setTags(updatedTags.filter((tag) => tag != null && tag !== ""))
  }

  const clearAllTags = () => {
    setTags([])
  }

  const onClickSubmit = () => {
    const formData = new FormData()
    fileList.forEach((file) => {
      formData.append("imageFile", file)
    })
    formData.append("id", visible.flag !== "add" ? scheduleInfo.id : null)
    formData.append("name", name)
    formData.append("description", description)
    formData.append("classId", gym)
    formData.append("clubId", club)
    formData.append(
      "dateTime",
      date ? dayjs(date).format("YYYY-MM-DDTHH:mm:ss") : "",
    )
    formData.append("serviceId", service)
    formData.append("employId", trainer)
    formData.append("duration", duration?.time)
    formData.append("imageUrl", imgUrl)
    formData.append("limit", limit)
    formData.append("price", price || null)

    const filteredTags = tags
      .filter((tag) => tag != null && tag !== "")
      .map((tag) => (typeof tag === "string" ? tag.trim() : String(tag)))
      .filter((tag) => tag !== "")

    formData.append("tags", filteredTags.join(", "))

    const filteredBenefits = benefits
      .filter(
        (benefit) =>
          benefit.BenefitText &&
          benefit.BenefitText.trim() !== "" &&
          benefit.BenefitDescription &&
          benefit.BenefitDescription.trim() !== "",
      )
      .map((benefit) => ({
        BenefitText: benefit.BenefitText.trim(),
        BenefitDescription: benefit.BenefitDescription.trim(),
        JobId: visible.flag !== "add" ? scheduleInfo.id : null,
      }))

    if (filteredBenefits.length > 0) {
      const benefitsArray = filteredBenefits.map(
        (b) =>
          `{"BenefitText":"${b.BenefitText}","BenefitDescription":"${b.BenefitDescription}","JobId":${b.JobId}}`,
      )
      const benefitsString = `[${benefitsArray.join(",")}]`
      formData.append("benefits", benefitsString)
    } else {
      formData.append("benefits", "[]")
    }

    setUploading(true)
    dispatch(
      scheduleAction({
        flag: visible.flag === "add" ? "put" : "post",
        value: formData,
      }),
    ).then(() => {
      dispatch(scheduleAction({ flag: "get", value: { ...payload } })).then(
        () => {
          setVisible({ open: false, flag: "" })
          setPage(1)
          setPageSize(20)
          setUploading(false)
        },
      )
    })
  }

  useEffect(() => {
    if (visible.flag === "edit" && scheduleInfo) {
      setName(scheduleInfo?.name)
      setDescription(scheduleInfo?.description || "")
      setClub(
        clubs?.data?.value.find((el) => el.label === scheduleInfo?.clubName)
          ?.value,
      )
      setService(
        abonementServices?.data?.value.find(
          (el) => el.label === scheduleInfo?.serviceName,
        )?.value,
      )
      setTrainer(
        trainersList?.data?.value.find(
          (el) => el.label === scheduleInfo?.employName,
        )?.value,
      )
      setGym(
        gyms?.data?.value.find((el) => el.label === scheduleInfo?.className)
          ?.value,
      )
      setLimit(scheduleInfo?.limit)
      setImgUrl(scheduleInfo?.imageUrl?.props?.src)
      setPrice(scheduleInfo?.price || null)
      setFileList([])

      if (scheduleInfo?.tags) {
        let parsedTags = []

        if (Array.isArray(scheduleInfo.tags)) {
          if (scheduleInfo.tags.length > 0) {
            if (typeof scheduleInfo.tags[0] === "string") {
              let tagsString = scheduleInfo.tags[0]
              tagsString = tagsString
                .replace(/\[/g, "")
                .replace(/\]/g, "")
                .replace(/"/g, "")
                .replace(/'/g, "")
              parsedTags = tagsString
                .split(/[,\n]/)
                .map((tag) => tag.trim())
                .filter((tag) => tag !== "" && tag !== "[" && tag !== "]")
            } else {
              parsedTags = scheduleInfo.tags
                .map((tag) =>
                  typeof tag === "string"
                    ? tag.trim().replace(/"/g, "").replace(/'/g, "")
                    : String(tag),
                )
                .filter((tag) => tag !== "" && tag !== "[" && tag !== "]")
            }
          }
        } else if (typeof scheduleInfo.tags === "string") {
          let tagsString = scheduleInfo.tags
          tagsString = tagsString
            .replace(/\[/g, "")
            .replace(/\]/g, "")
            .replace(/"/g, "")
            .replace(/'/g, "")
          parsedTags = tagsString
            .split(/[,\n]/)
            .map((tag) => tag.trim())
            .filter((tag) => tag !== "" && tag !== "[" && tag !== "]")
        }

        setTags(parsedTags)
      } else {
        setTags([])
      }

      if (scheduleInfo?.benefits && Array.isArray(scheduleInfo.benefits)) {
        setBenefits(scheduleInfo.benefits)
      } else {
        setBenefits([{ BenefitText: "", BenefitDescription: "" }])
      }
    }
  }, [visible, scheduleInfo, clubs, trainersList, abonementServices, gyms])

  const handleChange = (info) => {
    if (info.fileList.length === 0) {
      setFileList([])
    } else {
      let newFileList = [...fileList]
      newFileList = newFileList.slice(-1)

      setFileList(newFileList)
    }
  }

  const props = {
    accept: ".png,.jpg,.jpeg",
    onRemove: () => {
      setFileList([])
    },
    onChange: handleChange,
    beforeUpload: (file) => {
      if (file.size > 1048576) {
        openNotification({
          type: "error",
          message: "Размер файла должен быть меньше одного мегабайта",
        })
        return false
      }
      setFileList([...fileList, file])
      return false
    },
    fileList,
  }

  return (
    <CModal
      alignment='center'
      visible={visible.open}
      aria-labelledby='VerticallyCenteredExample'
      onClose={() => {
        setVisible({ open: false, flag: "" })
      }}
      keyboard={true}
      size='xl'
    >
      <CModalHeader>
        <CModalTitle id='VerticallyCenteredExample'>
          {visible.flag === "add"
            ? "Добавить расписание"
            : "Редактировать расписание"}
        </CModalTitle>
      </CModalHeader>
      <CModalBody
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <Flex vertical gap={16} style={{ marginTop: "20px" }}>
          <>
            <div>
              <Typography.Title level={5}>Наименование</Typography.Title>
              <Input
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
              />
            </div>

            <div>
              <Typography.Title level={5}>Описание</Typography.Title>
              <TextArea
                value={description}
                onChange={(e) => setDescription(e.currentTarget.value)}
                placeholder='Введите описание'
                rows={4}
              />
            </div>

            <div>
              <Typography.Title level={5}>Теги</Typography.Title>

              {tags.length > 0 && (
                <div style={{ marginBottom: "10px" }}>
                  <Space wrap size={[0, 8]}>
                    {tags.map((tag, index) => (
                      <Tag
                        key={index}
                        closable
                        onClose={() => removeTag(index)}
                        style={{ marginBottom: "5px" }}
                      >
                        {tag}
                      </Tag>
                    ))}
                    {tags.length > 0 && (
                      <Button
                        type='text'
                        size='small'
                        onClick={clearAllTags}
                        style={{ padding: "0 5px", fontSize: "12px" }}
                      >
                        Очистить все
                      </Button>
                    )}
                  </Space>
                </div>
              )}

              <Input
                value={tagInput}
                onChange={handleTagInputChange}
                onKeyDown={handleTagInputKeyDown}
                onBlur={addTags}
                placeholder='Введите теги через запятую и нажмите Enter'
              />

              <div
                style={{ marginTop: "5px", fontSize: "12px", color: "#666" }}
              >
                Введите теги через запятую и нажмите Enter
              </div>
            </div>

            {/* <div>
              <Typography.Title level={5}>
                Преимущества тренировок
                <Button
                  type='dashed'
                  onClick={addBenefit}
                  icon={<PlusOutlined />}
                  style={{ marginLeft: "10px" }}
                >
                  Добавить преимущество
                </Button>
              </Typography.Title>

              {benefits.map((benefit, index) => (
                <Card
                  key={index}
                  size='small'
                  style={{ marginBottom: "10px" }}
                  extra={
                    benefits.length > 1 && (
                      <Button
                        type='text'
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeBenefit(index)}
                      />
                    )
                  }
                >
                  <Form layout='vertical'>
                    <Form.Item label='Заголовок преимущества'>
                      <Input
                        value={benefit?.BenefitText || ""}
                        onChange={(e) =>
                          handleBenefitChange(
                            index,
                            "BenefitText",
                            e.target.value,
                          )
                        }
                        placeholder='Например: Индивидуальный подход'
                      />
                    </Form.Item>
                    <Form.Item label='Описание преимущества'>
                      <TextArea
                        value={benefit?.BenefitDescription || ""}
                        onChange={(e) =>
                          handleBenefitChange(
                            index,
                            "BenefitDescription",
                            e.target.value,
                          )
                        }
                        placeholder='Подробное описание преимущества'
                        rows={3}
                      />
                    </Form.Item>
                  </Form>
                </Card>
              ))}
            </div> */}

            <div>
              <Typography.Title level={5}>Дата</Typography.Title>
              <DatePicker
                value={date}
                onChange={(e) => setDate(e)}
                style={{ width: "100%" }}
                showTime
              />
            </div>

            <div>
              <Typography.Title level={5}>Длительность</Typography.Title>
              <TimePicker
                value={duration?.value}
                onChange={(e, b) => {
                  setDuration({ value: e, time: b })
                }}
                style={{ width: "100%" }}
              />
            </div>

            <div>
              <Typography.Title level={5}>Тренер</Typography.Title>
              <Select
                placeholder='Выбрать'
                style={{ width: "100%" }}
                value={trainer}
                onChange={(e) => setTrainer(e)}
                onClick={() => {
                  visible.flag === "add" && dispatch(getTrainers())
                }}
                options={trainersList?.data?.value}
              />
            </div>

            <div>
              <Typography.Title level={5}>Клуб</Typography.Title>
              <Select
                placeholder='Выбрать'
                style={{ width: "100%" }}
                value={club}
                onChange={(e) => setClub(e)}
                onClick={() => {
                  visible.flag === "add" && dispatch(getClubs())
                }}
                options={clubs?.data?.value}
              />
            </div>

            <div>
              <Typography.Title level={5}>Зал</Typography.Title>
              <Select
                placeholder='Выбрать'
                style={{ width: "100%" }}
                value={gym}
                onChange={(e) => setGym(e)}
                onClick={() => {
                  visible.flag === "add" && dispatch(getGyms())
                }}
                options={gyms?.data?.value}
              />
            </div>

            <div>
              <Typography.Title level={5}>Услуга</Typography.Title>
              <Select
                value={service}
                onChange={(e) => setService(e)}
                onClick={() => {
                  visible.flag === "add" && dispatch(getServices())
                }}
                style={{ width: "100%" }}
                options={abonementServices?.data?.value}
              />
            </div>

            <div>
              <Typography.Title level={5}>Лимит</Typography.Title>
              <Input
                value={limit}
                onChange={(e) => setLimit(e.currentTarget.value)}
                type='number'
              />
            </div>

            <div>
              <Typography.Title level={5}>Цена</Typography.Title>
              <Input
                value={price}
                onChange={(e) => setPrice(e.currentTarget.value)}
              />
            </div>

            <div>
              <Typography.Title level={5}>
                Ссылка на изображение
              </Typography.Title>
              <Input
                value={imgUrl}
                onChange={(e) => setImgUrl(e.currentTarget.value)}
              />
            </div>

            <div>
              <Typography.Title level={5}>
                Изображение (размер меньше 1 МБ)
              </Typography.Title>
              <Upload {...props}>
                <Button icon={<UploadOutlined />}>Выберите файл</Button>
              </Upload>
            </div>
          </>
        </Flex>
      </CModalBody>
      <CModalFooter>
        <CButton
          onClick={() => {
            setVisible({ open: false, flag: "" })
          }}
          className='text-light'
          color='secondary'
        >
          Закрыть
        </CButton>
        <CButton
          disabled={uploading}
          onClick={onClickSubmit}
          className='text-light'
        >
          {visible.flag === "add" ? "Добавить" : "Редактировать"}
        </CButton>
      </CModalFooter>
    </CModal>
  )
}

export default AddEditScheduleModal
